import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RECIPE_PATHS, validationSchema } from '../../constants';
import { connect } from 'react-redux';
import { Dispatch, RootState } from '../../../../store/store';
import { RecipeForm } from '../../Components/RecipeForm';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import { IPreHeatRecipeEditable, IRecipe } from '../../../../types/recipes-types';
import CycleSettings from '../../../../enums/cycle-setting.enum';
import { preHeatRecipeValidationSchema } from '../../../PreHeatRecipes/constants';
import { PreHeatRecipeForm } from '../../../PreHeatRecipes/Components/PreHeatRecipeForm';
import { useTranslation } from 'react-i18next';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const RecipeEdit: React.FC<ComponentProps> = ({ recipe, getRecipe, updateRecipe, reset }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();

  const onSubmitHandler = useCallback(
    async (data: IRecipe) => {
      await updateRecipe({ id, body: data });
      navigate(RECIPE_PATHS.BASE);
    },
    [id, updateRecipe, navigate],
  );

  const onPreHeatSubmitHandler = useCallback(
    async (data: IPreHeatRecipeEditable) => {
      await updateRecipe({ id, body: data });
      navigate(RECIPE_PATHS.BASE);
    },
    [id, updateRecipe, navigate],
  );

  useEffect(() => {
    reset();
    getRecipe(id);

    return () => {
      reset();
    };
  }, [id]);

  if (!recipe) {
    return (
      <div className="flex w-full justify-center">
        <SpinnerLoader />
      </div>
    );
  }

  return (
    <>
      {recipe.mode === CycleSettings.PostWeld ? (
        <RecipeForm
          initialValues={recipe}
          validationSchema={validationSchema}
          submitButtonName={t('buttons.update')}
          formName={t('labels.editRecipe')}
          onSubmit={onSubmitHandler}
          enableTemperatureUnitSwitcher
        />
      ) : (
        <PreHeatRecipeForm
          initialValues={recipe}
          validationSchema={preHeatRecipeValidationSchema}
          submitButtonName={t('buttons.update')}
          formName={t('labels.editRecipe')}
          onSubmit={onPreHeatSubmitHandler}
          enableTemperatureUnitSwitcher
        />
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  recipe: state.recipes.recipe,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getRecipe: dispatch.recipes.getRecipe,
  updateRecipe: dispatch.recipes.updateRecipe,
  reset: dispatch.recipes.resetState,
});

export default connect(mapState, mapDispatch)(RecipeEdit);
